import { Signal } from "@preact/signals";
import { omitIf } from "./object";

export function* loopArray<T>(array: T[]): Generator<T, T, boolean> {
    let index = 0;

    while (true) {
        const toBreak = yield array[index];

        index = (index + 1) % array.length;

        if (toBreak) {
            break;
        }
    }

    return array[index];
}

export const uniq = <T>(array: T[]) => [...new Set(array)];

export const uniqKey = <T, K extends keyof T>(array: T[], key: K) => [
    ...new Set(array.map((item) => item[key])),
];

export const uniqBy = <T, K extends keyof T>(array: T[], key: K) => {
    const seen = new Set();

    return array.filter((item) => {
        const value = item[key];

        if (seen.has(value)) {
            return false;
        }

        seen.add(value);
        return true;
    });
};

export const uniqByFn = <T>(
    array: readonly T[],
    fn: (left: T, right: T) => boolean,
) => {
    const result: T[] = [];

    for (const item of array) {
        if (!result.some((resultItem) => fn(resultItem, item))) {
            result.push(item);
        }
    }

    return result;
};

export const sum = (array: number[]) =>
    array.reduce((result, item) => result + item, 0);

export const sumBy = <T, K extends keyof T>(array: T[], key: K) =>
    array.reduce((result, item) => result + Number(item[key]), 0);

export const avg = (array: number[]) =>
    array.length > 0 ? sum(array) / array.length : 0;

export const avgBy = <T, K extends keyof T>(array: T[], key: K) =>
    array.length > 0 ? sumBy(array, key) / array.length : 0;

export const chunk = <T>(array: T[], size: number) =>
    Array.from(
        {
            length: Math.ceil(array.length / size),
        },
        (_, index) => array.slice(index * size, (index + 1) * size),
    );

export const upsert = <T extends object, K extends keyof T>(
    array: T[],
    newItems: readonly T[],
    key: K,
) => {
    const map = new Map(array.map((item) => [item[key], item]));

    for (const item of newItems) {
        const existing = map.get(item[key]);

        if (existing) {
            Object.assign(
                existing,
                omitIf(item, (v) => v instanceof Signal),
            );
        } else {
            array.push(item);
        }
    }

    return array;
};

export const upsertWithTag = <
    T extends { tag: Signal<number> },
    K extends keyof T,
>(
    array: T[],
    newItems: readonly T[],
    key: K,
) => {
    const map = new Map(array.map((item) => [item[key], item]));

    for (const item of newItems) {
        const existing = map.get(item[key]);

        if (existing) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { tag: _, ...rest } = item;

            Object.assign(existing, rest);
            existing.tag.value = existing.tag.peek() + 1;
        } else {
            array.push(item);
        }
    }

    return array;
};

export const upsertOne = <T, K extends keyof T>(
    array: T[],
    newItem: T,
    key: K,
) => {
    const index = array.findIndex((item) => item[key] === newItem[key]);

    if (index !== -1) {
        array[index] = newItem;
    } else {
        array.push(newItem);
    }

    return array;
};

export const maxOf = (arr: number[]) => {
    let max = arr[0];

    for (let i = 1; i < arr.length; ++i) {
        if (arr[i] > max) {
            max = arr[i];
        }
    }

    return max;
};

export const minOf = (arr: number[]) => {
    let min = arr[0];

    for (let i = 1; i < arr.length; ++i) {
        if (arr[i] < min) {
            min = arr[i];
        }
    }

    return min;
};

export const minMaxOf = (arr: number[]) => {
    let min = arr[0];
    let max = arr[0];

    for (let i = 1; i < arr.length; ++i) {
        if (arr[i] < min) {
            min = arr[i];
        } else if (arr[i] > max) {
            max = arr[i];
        }
    }

    return [min, max] as const;
};

export const pushSorted = <T>(
    array: T[],
    item: T,
    compareFn: (a: T, b: T) => number,
) => {
    let low = 0;
    let high = array.length;

    while (low < high) {
        const mid = (low + high) >>> 1;

        if (compareFn(array[mid], item) < 0) {
            low = mid + 1;
        } else {
            high = mid;
        }
    }

    array.splice(low, 0, item);
};
